import { useState } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "../Forms/components/ButtonMenu.js";
import { usePermissions } from "../../hooks/usePermissions";
import LoggingAPI from "../../api/logging/index.js";
import ForensicSoftwareAPI from "../../api/software/index.js";
import { ForensicSoftware } from "@/types/index.js";
import { SoftwareFormData } from "../Forms/DefaultSoftwareForm.js";

interface DeleteSoftwareModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (defaultInfo?: SoftwareFormData) => void;
  onCancel: () => void;
  buttonProps?: {
    cancelText: string;
    submitText: string;
  };
  defaultInfo?: ForensicSoftware;
}

export const DeleteSoftwareModal: React.FC<DeleteSoftwareModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { currentUser } = usePermissions();

  const handleSubmit = async () => {
    setSubmitting(true);

    await ForensicSoftwareAPI.deleteSoftwareItem(defaultInfo?.license_id);

    LoggingAPI.createLog({
      user_id: currentUser.user_id,
      message: `Deleted software: ${defaultInfo?.name}`,
      event: "forensic_software:delete",
      object_type: "forensic_software",
    });

    onSubmit?.(defaultInfo);
    setSubmitting(false);
    onClose?.();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Delete Forensic Software</Modal.Title>
      <div style={{ fontWeight: 600, margin: "20px 0px" }}>
        Are you sure you want to delete this software item:{" "}
        <span style={{ fontWeight: 600 }}>"{defaultInfo?.software_name}"</span>?
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Delete Software"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
